// src/screens/BuyerScreen.tsx

import React, {
    useContext,
    useEffect,
    useMemo,
    useState,
    useRef,
    useCallback,
} from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import NavigationBar, {
    APP_BASE_WIDTH,
    APP_BASE_HEIGHT,
} from '../../nav/NavigationBar'; // ✅ 공통 기준 가져오기
import { ChevronLeft, X } from 'lucide-react';
import WebApp from '@twa-dev/sdk';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

import { NetworkContext } from '../../context/NetworkContext';
import { DefaultTokenContext } from '../../context/DefaultTokenContext';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../store';
import useSecureStorage from '../../hooks/useSecureStorage';
import useQssWalletAddress, { QssWalletAddress } from '../../hooks/useQssWalletAddress';
import i18next from 'i18next';
import PinCodeScreen from '../Authentication/PinCodeScreen';
import { tgShowAlert } from '../../utils/telegramPopup';
import QposSocket from '../../nav/QposSocket';

type Step = 'scan' | 'amount' | 'qpin';

interface ShopInfo {
    name: string;
    imageUrl: string;
    availableAmount: string;
}

interface GhostChar {
    char: string;
    x: number;
    y: number;
    isDecimal: boolean;
}

// ✅ NavigationBar에서 쓰는 기준 해상도와 동일하게 사용
const BASE_WIDTH = APP_BASE_WIDTH;
const BASE_HEIGHT = APP_BASE_HEIGHT;

const FOOTER_HEIGHT = 100;
const MAX_WIDTH = BASE_WIDTH;
const API_BASE_URL =
    process.env.REACT_APP_QWALLET_SERVER_URL || 'http://192.168.0.123:5320';

interface SalesProfileResponse {
    name?: string;
    store_name?: string;
    shop_name?: string;
    image_url?: string;
    store_image_url?: string;
    shop_image_url?: string;
    data?: any;
}

const TOKEN_ADDRESS = '0x25D9f44e29444c8985a3fA0B887B76C0D51a55F6'; // ELYM 토큰 주소

const BuyerScreen: React.FC = () => {
    const { theme } = useContext(ThemeContext);
    const navigate = useNavigate();

    const [step, setStep] = useState<Step>('scan');
    const [shop, setShop] = useState<ShopInfo | null>(null);
    const [amount, setAmount] = useState<string>('');
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [scannedText, setScannedText] = useState<string>('');
    const [isApprovedModalOpen, setIsApprovedModalOpen] = useState(false);
    const [waitingForQpin, setWaitingForQpin] = useState(false);

    const [lastAction, setLastAction] = useState<'add' | null>(null);
    const [ghost, setGhost] = useState<GhostChar | null>(null);
    const amountContainerRef = useRef<HTMLDivElement | null>(null);
    const lastCharRef = useRef<HTMLSpanElement | null>(null);

    const isMobile = useMemo(() => {
        if (typeof window === 'undefined') return false;
        return /Mobi|Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent);
    }, []);

    const buyPending = useSelector((state: RootState) => state.qpin.buyPending);
    const buyConfirm = useSelector((state: RootState) => state.qpin.buyConfirm);

    const qPin = useMemo(() => {
        if (!buyPending) return null;

        const src = (buyPending as any).data ?? buyPending;

        const raw =
            src.qpin ??
            src.qPin ??
            src.q_pin ??
            src.pin;

        if (raw === undefined || raw === null || raw === '') return null;

        return String(raw);
    }, [buyPending]);

    useEffect(() => {
        if (!waitingForQpin) return;
        if (!buyPending) return;

        console.log('✅ [BuyerScreen] buyPending arrived while waiting. Go qpin.');
        setStep('qpin');
        setWaitingForQpin(false);
    }, [waitingForQpin, buyPending]);

    useEffect(() => {
        if (!buyConfirm) return;

        console.log('✅ [BuyerScreen] buyConfirm arrived:', buyConfirm);

        try {
            const confirmKey = JSON.stringify(buyConfirm);
            const handledKey = sessionStorage.getItem('buyer_last_confirm_key');
            if (handledKey === confirmKey) {
                console.log(
                    '✅ [BuyerScreen] buyConfirm already handled. Skip modal.'
                );
                return;
            }
            setIsApprovedModalOpen(true);
        } catch (err) {
            console.error('❌ [BuyerScreen] buyConfirm check failed:', err);
            setIsApprovedModalOpen(true);
        }
    }, [buyConfirm]);

    const {
        sendTransactionWithPrivateKey,
        getTokenBalance,
    } = useContext(NetworkContext);
    const { searchToken } = useContext(DefaultTokenContext);

    let dispatch = useDispatch();
    const { t } = useTranslation();

    const { getWalletAddress } = useSecureStorage();
    const { getQssWalletAddress } = useQssWalletAddress();
    const location = useLocation();
    const { retrieveEncryptedData, getBlockChain, getNetworkCurrency } =
        useSecureStorage();

    const [withdrawAddress, setWithdrawAddress] = useState('');
    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [gasFee, setGasFee] = useState<string | undefined>('0.0');

    const [showPinCodeAuth, setShowPinCodeAuth] = useState(false);
    let isStartWithdrawal = false;

    const decorateShopWithBalance = useCallback(
        async (base: ShopInfo): Promise<ShopInfo> => {
            try {
                const buyerWallet = await getWalletAddress();
                if (!buyerWallet) {
                    return {
                        ...base,
                        availableAmount: base.availableAmount || '0 ELYM',
                    };
                }

                const rawBalance: any = await getTokenBalance(
                    buyerWallet,
                    TOKEN_ADDRESS,
                    true,
                    'promise'
                );

                let display = '';
                const num = Number(rawBalance);
                if (!Number.isNaN(num)) {
                    display = `${num.toLocaleString('ko-KR')} ELYM`;
                } else {
                    display = `${String(rawBalance)} ELYM`;
                }

                return {
                    ...base,
                    availableAmount: display,
                };
            } catch (err) {
                console.error('💰 getTokenBalance error:', err);
                return {
                    ...base,
                    availableAmount: base.availableAmount || '0 ELYM',
                };
            }
        },
        [getWalletAddress, getTokenBalance]
    );

    const fetchShopInfo = useCallback(
        async (walletAddress: string): Promise<ShopInfo | null> => {
            try {
                const res = await axios.post<SalesProfileResponse>(
                    `${API_BASE_URL}/qpos/sales-profile/select`,
                    {
                        wallet_address: walletAddress,
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    }
                );

                console.log('sales-profile/select response:', res.data);

                const raw: SalesProfileResponse = res.data || {};
                const body: any = (raw as any).data || raw;

                const name =
                    body.name ?? body.store_name ?? body.shop_name ?? '';

                const imagePath =
                    body.image_url ?? body.store_image_url ?? body.shop_image_url ?? '';
                const imageUrl = imagePath
                    ? `${API_BASE_URL}/public${imagePath}`
                    : '/img/해피브릿지.png';

                const availableAmount = '0 ELYM';

                return {
                    name,
                    imageUrl,
                    availableAmount,
                };
            } catch (e) {
                console.error('fetchShopInfo error', e);
                return null;
            }
        },
        []
    );

    const handleQrResult = useCallback(
        async (text: string) => {
            const walletAddress = text.trim();
            setScannedText(walletAddress);

            if (!walletAddress) {
                if (WebApp.showAlert) {
                    WebApp.showAlert('QR 코드에서 지갑 주소를 찾을 수 없습니다.');
                } else {
                    alert('QR 코드에서 지갑 주소를 찾을 수 없습니다.');
                }
                return;
            }

            const shopInfo = await fetchShopInfo(walletAddress);

            if (!shopInfo) {
                if (WebApp.showAlert) {
                    WebApp.showAlert(
                        '등록되지 않은 판매자이거나 상점 정보를 찾을 수 없습니다.1'
                    );
                } else {
                    alert(
                        '등록되지 않은 판매자이거나 상점 정보를 찾을 수 없습니다.2'
                    );
                }
                setShop(null);
                setStep('scan');
                return;
            }

            const shopWithBalance = await decorateShopWithBalance(shopInfo);

            setShop(shopWithBalance);
            setStep('amount');
        },
        [fetchShopInfo, decorateShopWithBalance]
    );

    useEffect(() => {
        if (!isMobile) return;
        if (step !== 'scan') return;

        let scanned = false;

        const callback = (text: string): true | void => {
            scanned = true;
            console.log('TgWebAppQRCodeCameraCallback :', text);

            void handleQrResult(text);

            return true;
        };

        try {
            WebApp.showScanQrPopup(
                { text: '판매점 QR코드를 촬영해주세요.' },
                callback
            );
        } catch (e) {
            console.error('showScanQrPopup error', e);
        }

        const handlePopupClosed = () => {
            if (!scanned && step === 'scan') {
                console.log(
                    'QR popup closed without scan (mobile). Stay on scan page.'
                );
            }
        };

        // @ts-ignore
        WebApp.onEvent?.('popupClosed', handlePopupClosed);

        return () => {
            // @ts-ignore
            WebApp.offEvent?.('popupClosed', handlePopupClosed);
        };
    }, [isMobile, step, handleQrResult]);

    const handleNext = () => {
        const mockShop: ShopInfo = {
            name: '해피브릿지 붕어빵집',
            imageUrl: '/img/해피브릿지.png',
            availableAmount: '0 ELYM',
        };

        (async () => {
            const shopWithBalance = await decorateShopWithBalance(mockShop);
            setScannedText('');
            setShop(shopWithBalance);
            setStep('amount');
        })();
    };

    const handleScanRetry = () => {
        if (!isMobile) return;

        let scanned = false;

        const callback = (text: string): true | void => {
            scanned = true;
            console.log('TgWebAppQRCodeCameraCallback (retry) :', text);

            void handleQrResult(text);

            return true;
        };

        try {
            WebApp.showScanQrPopup(
                { text: '판매점 QR코드를 촬영해주세요.' },
                callback
            );
        } catch (e) {
            console.error('showScanQrPopup retry error', e);
        }

        const handlePopupClosed = () => {
            if (!scanned && step === 'scan') {
                console.log('QR popup closed on retry.');
            }
        };

        // @ts-ignore
        WebApp.onEvent?.('popupClosed', handlePopupClosed);

        setTimeout(() => {
            // @ts-ignore
            WebApp.offEvent?.('popupClosed', handlePopupClosed);
        }, 1000);
    };

    const handleKeyPress = (key: string) => {
        if (key === 'backspace') {
            if (!amount) return;

            const last = amount[amount.length - 1];

            let isDecimal = false;
            const dotIndex = amount.indexOf('.');
            if (dotIndex !== -1 && amount.length - 1 >= dotIndex) {
                isDecimal = true;
            }

            if (amountContainerRef.current && lastCharRef.current) {
                const containerRect =
                    amountContainerRef.current.getBoundingClientRect();
                const lastRect = lastCharRef.current.getBoundingClientRect();

                const x = lastRect.left - containerRect.left;
                const y = lastRect.top - containerRect.top;

                setGhost({
                    char: last,
                    x,
                    y,
                    isDecimal,
                });
            }

            setAmount((prev) => prev.slice(0, -1));
            setLastAction(null);
            return;
        }

        setAmount((prev) => {
            if (key === '.') {
                const dotIndex = prev.indexOf('.');

                if (dotIndex !== -1) {
                    const newVal = prev.slice(0, dotIndex);
                    setLastAction(null);
                    return newVal;
                }

                if (!prev) {
                    setLastAction('add');
                    return '0.';
                }
                if (prev.endsWith('.')) return prev;

                setLastAction('add');
                return prev + '.';
            }

            if (!/^\d$/.test(key)) return prev;

            const hasDot = prev.includes('.');

            if (!hasDot && prev.length >= 12) return prev;

            setLastAction('add');

            if (prev === '0' && !hasDot) {
                return key;
            }

            return prev + key;
        });
    };

    const hasAmount =
        amount.length > 0 &&
        !Number.isNaN(Number(amount)) &&
        Number(amount) > 0;

    const { intFormatted, decPart, hasDot } = (() => {
        if (!amount) {
            return { intFormatted: '0', decPart: '', hasDot: false };
        }

        const dotIndex = amount.indexOf('.');
        const hasDotLocal = dotIndex !== -1;

        if (!hasDotLocal) {
            const intNumber = Number(amount || '0');
            return {
                intFormatted: intNumber.toLocaleString('ko-KR'),
                decPart: '',
                hasDot: false,
            };
        }

        const rawInt = amount.slice(0, dotIndex) || '0';
        const rawDec = amount.slice(dotIndex + 1);

        const intNumber = Number(rawInt || '0');
        const formattedInt = intNumber.toLocaleString('ko-KR');

        return {
            intFormatted: formattedInt,
            decPart: rawDec,
            hasDot: true,
        };
    })();

    const summaryAmountStr = (() => {
        if (!amount) return '0';
        if (!hasDot) return intFormatted;
        return `${intFormatted}.${decPart}`;
    })();

    const handlePay = () => {
        if (!hasAmount) return;

        setWithdrawAddress(scannedText);
        setWithdrawAmount(amount);
        setShowPinCodeAuth(true);
    };

    const handleWithdraw = async (pin: string) => {
        if (true === isStartWithdrawal) {
            await tgShowAlert(
                i18next.t('template_withdraw_screen_message_25')
            );
            return;
        }

        isStartWithdrawal = true;

        let privateKey: any;
        let publicKey: any;
        let networkType = '';

        try {
            const network = await getBlockChain();
            networkType = network;

            const data = await retrieveEncryptedData(network, pin);
            privateKey = data.privateKey;
            publicKey = data.publicKey;
            if (!privateKey) {
                tgShowAlert(
                    i18next.t('template_withdraw_screen_message_22')
                );
                return;
            }
        } catch (error) {
            console.log('Error handleWithdraw', error);
            tgShowAlert(i18next.t('template_withdraw_screen_message_22'));
            return;
        }

        setIsProcessing(true);

        try {
            const receipt = await sendTransactionWithPrivateKey(
                privateKey,
                withdrawAddress,
                withdrawAmount,
                'elym',
                TOKEN_ADDRESS,
                publicKey || ''
            );

            console.log('토큰 전송 receipt : ', receipt);
            if (!receipt || receipt === '') {
                setIsProcessing(false);
                await tgShowAlert(
                    i18next.t('template_withdraw_screen_message_24')
                );
                return;
            }

            const padZero = (value: number) => {
                return value < 10 ? `0${value}` : `0${value}`.slice(-2);
            };

            const date = new Date();

            const year = date.getFullYear();
            const month = padZero(date.getMonth() + 1);
            const day = padZero(date.getDate());
            const hours = padZero(date.getHours());
            const minutes = padZero(date.getMinutes());
            const seconds = padZero(date.getSeconds());

            const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

            const txId = receipt ? receipt : '';

            const withdrawData = {
                address: withdrawAddress,
                amount: `${withdrawAmount}`,
                fee: `${gasFee}`,
                date: formattedDate,
                txid: txId,
                symbol: 'elym',
            };

            try {
                const step1Res = await axios.post(
                    `${API_BASE_URL}/qpos/qpin/step1`,
                    {
                        tx_hash: txId,
                        network_type: networkType,
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    }
                );

                console.log('qpin/step1 response:', step1Res.data);
            } catch (err) {
                console.error('qpin step1 호출 실패:', err);
            }

            setTimeout(() => {
                setIsProcessing(false);
                setWaitingForQpin(true);
            }, 1000);
        } catch (error: any) {
            setIsProcessing(false);
            if (error.code === 'INSUFFICIENT_FUNDS') {
                alert(i18next.t('template_withdraw_screen_message_20'));
            } else {
                alert(i18next.t('template_withdraw_screen_message_24'));
            }
        } finally {
            isStartWithdrawal = false;
        }
    };

    const handlePinCodeintAuthSuccess = (pin: string) => {
        setShowPinCodeAuth(false);
        handleWithdraw(pin);
    };

    const [viewportHeight, setViewportHeight] = useState<number | null>(null);
    const [viewportWidth, setViewportWidth] = useState<number | null>(null);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handleResize = () => {
            setViewportHeight(window.innerHeight);
            setViewportWidth(window.innerWidth);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const baseHeight = BASE_HEIGHT;

    const amountImageMaxHeight = Math.max(140, baseHeight * 0.2);
    const qpinImageMaxHeight = Math.max(140, baseHeight * 0.2);

    const isCompactHeight = (viewportHeight ?? BASE_HEIGHT) < 600;

    // ✅ NavigationBar / SellerScreen 과 동일한 스케일 계산
    const scale = useMemo(() => {
        if (!viewportWidth || !viewportHeight) return 1;
        const widthRatio = viewportWidth / BASE_WIDTH;
        const heightRatio = viewportHeight / BASE_HEIGHT;
        const ratio = Math.min(widthRatio, heightRatio);
        return ratio < 1 ? 1 : ratio;
    }, [viewportWidth, viewportHeight]);

    const showFooter = step === 'scan';
    const isQpinPhase = step === 'qpin';

    const handleBackToScan = () => {
        setAmount('');
        setIsProcessing(false);
        setShop(null);
        setScannedText('');
        setWaitingForQpin(false);
        setStep('scan');
    };

    // ✅ 공통 뒤로가기 처리
    // - step === 'amount' → QR찍기(step 'scan')로 이동
    // - step === 'scan' 또는 'qpin' → /BalanceScreen 으로 이동
    const handleBackNavigation = useCallback(() => {
        if (step === 'amount') {
            handleBackToScan();
            return;
        }
        // scan 또는 qpin 단계
        navigate('/BalanceScreen');
    }, [step, navigate]);

    const handleApproveConfirm = () => {
        if (buyConfirm) {
            try {
                const confirmKey = JSON.stringify(buyConfirm);
                sessionStorage.setItem('buyer_last_confirm_key', confirmKey);
            } catch (err) {
                console.error(
                    '❌ [BuyerScreen] failed to save handled buyConfirm:',
                    err
                );
            }
        }

        setIsApprovedModalOpen(false);
        navigate('/BalanceScreen');
    };

    // 브라우저/디바이스 뒤로가기(popstate) 가로채기
    useEffect(() => {
        // 첫 진입 시 현재 페이지를 한 번 쌓아두기
        window.history.pushState(null, '', window.location.href);

        const onPopState = (event: PopStateEvent) => {
            event.preventDefault?.();
            handleBackNavigation();
            // 뒤로가기 소비 후 다시 현재 상태를 push 해서
            // 다음 뒤로가기도 동일 로직을 거치도록 함
            window.history.pushState(null, '', window.location.href);
        };

        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [handleBackNavigation]);

    const containerStyle: React.CSSProperties = {
        height: '100vh',
        maxHeight: '100vh',
        overflow: 'hidden',
        backgroundColor: theme.background_01,
        color: theme.text_01,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    };

    const scaledRootStyle: React.CSSProperties = {
        width: BASE_WIDTH,
        height: BASE_HEIGHT,
        transform: `scale(${scale})`,
        transformOrigin: 'top center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    };

    const contentOuterStyle: React.CSSProperties = {
        width: '100%',
        maxWidth: MAX_WIDTH,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        padding: '0 16px',
    };

    const headerStyle: React.CSSProperties = {
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    };

    const headerLeftGroupStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    };

    const headerButtonStyle: React.CSSProperties = {
        width: 32,
        height: 32,
        borderRadius: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    };

    const headerSpacerStyle: React.CSSProperties = {
        width: 32,
        height: 32,
    };

    const coinCircleStyle: React.CSSProperties = {
        width: 28,
        height: 28,
        borderRadius: 10,
        backgroundColor: theme.background_02 ?? '#222',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
    };

    const coinImage: React.CSSProperties = {
        width: 24,
        height: 24,
        objectFit: 'cover',
    };

    const headerTitle: React.CSSProperties = {
        fontSize: 14,
        fontWeight: 500,
    };

    const scanOuterStyle: React.CSSProperties = {
        width: '100%',
        maxWidth: MAX_WIDTH,
        height: BASE_HEIGHT - FOOTER_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 16px',
        boxSizing: 'border-box',
    };

    const scanTitleStyle: React.CSSProperties = {
        fontSize: 18,
        fontWeight: 600,
        textAlign: 'center',
        marginBottom: 16,
    };

    const qrFrameStyle: React.CSSProperties = {
        width: '70%',
        maxWidth: 260,
        aspectRatio: '1 / 1',
        borderRadius: 24,
        border: '4px solid rgba(255,255,255,0.8)',
        boxSizing: 'border-box',
    };

    const helperTextStyle: React.CSSProperties = {
        fontSize: 13,
        color: theme.text_03 ?? '#9f9f9f',
        textAlign: 'center',
        marginTop: 16,
    };

    const qpinHelperTextStyle: React.CSSProperties = {
        fontSize: 16,
        fontWeight: 600,
        color: theme.text_01,
        textAlign: 'center',
    };

    const scanButtonsRow: React.CSSProperties = {
        marginTop: 24,
        display: 'flex',
        justifyContent: 'center',
        gap: 10,
        width: '100%',
        maxWidth: 320,
    };

    const primaryButtonStyle: React.CSSProperties = {
        flex: 1,
        height: 44,
        borderRadius: 16,
        border: 'none',
        outline: 'none',
        backgroundColor: '#2f88ff',
        color: '#ffffff',
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
    };

    const secondaryButtonStyle: React.CSSProperties = {
        flex: 1,
        height: 44,
        borderRadius: 16,
        border: `1px solid ${theme.line_01 ?? '#555'}`,
        outline: 'none',
        backgroundColor: 'transparent',
        color: '#ffffff',
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
    };

    const contentInnerFullHeight: React.CSSProperties = {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    };

    const shopTopWrapper: React.CSSProperties = {
        flex: 0,
    };

    const shopImageStyle: React.CSSProperties = {
        width: '100%',
        maxWidth: 450,
        height: '30vh',
        maxHeight: 260,
        borderRadius: 10,
        objectFit: 'cover',
        backgroundColor: '#333',
    };

    const shopImageAmountStyle: React.CSSProperties = {
        ...shopImageStyle,
        height: 'auto',
        minHeight: 140,
        maxHeight: amountImageMaxHeight,
        marginBottom: '5px',
    };

    const shopImageQpinStyle: React.CSSProperties = {
        ...shopImageStyle,
        height: 'auto',
        minHeight: 140,
        maxHeight: qpinImageMaxHeight,
    };

    const askTextStyle: React.CSSProperties = {
        fontSize: 15,
    };

    const askRowCompactStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
    };

    const amountDisplayWrapper: React.CSSProperties = {
        padding: '8px 4px 4px',
        minHeight: 40,
        display: 'flex',
        alignItems: 'baseline',
        borderBottom: `1px solid ${theme.line_01 ?? '#333'}`,
        position: 'relative',
    };

    const amountDisplayTextInt: React.CSSProperties = {
        fontSize: 28,
        fontWeight: 700,
    };

    const amountDisplayTextDec: React.CSSProperties = {
        fontSize: 18,
        fontWeight: 500,
    };

    const amountDisplayTextUnit: React.CSSProperties = {
        fontSize: 16,
        fontWeight: 500,
        marginLeft: 6,
        opacity: 0.9,
    };

    const zeroPlaceholderStyle: React.CSSProperties = {
        fontSize: 28,
        fontWeight: 400,
        opacity: 0.4,
        color: theme.text_01,
    };

    const cursorStyle: React.CSSProperties = {
        width: 2,
        height: 22,
        backgroundColor: theme.text_01,
        animation: 'cursor-blink 1s step-end infinite',
    };

    const amountCharNewStyle: React.CSSProperties = {
        animation: 'buyer-drop-in 0.18s ease-out',
    };

    const ghostCharBaseStyle: React.CSSProperties = {
        position: 'absolute',
        animation: 'buyer-fly-up 0.18s ease-out forwards',
        pointerEvents: 'none',
    };

    const keypadAndButtonWrapper: React.CSSProperties = {
        marginTop: 'auto',
        paddingTop: 12,
    };

    const keypadWrapper: React.CSSProperties = {
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
    };

    const keypadGrid: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        columnGap: 32,
        justifyItems: 'center',
        paddingBottom: 15,
    };

    const keypadButton: React.CSSProperties = {
        height: 55,
        minWidth: 56,
        width: '100%',
        border: 'none',
        outline: 'none',
        color: '#ffffff',
        fontSize: 28,
        fontWeight: 400,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    const decimalKeyActiveStyle: React.CSSProperties = {
        backgroundColor: '#2f88ff',
    };

    const keypadEmptyCell: React.CSSProperties = {
        height: 40,
    };

    const bottomButtonWrapper: React.CSSProperties = {
        marginBottom: 16,
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
        height: 44,
        display: 'flex',
        alignItems: 'center',
    };

    const bottomButtonStyle: React.CSSProperties = {
        width: '100%',
        height: 44,
        borderRadius: 16,
        border: 'none',
        outline: 'none',
        backgroundColor: '#2f88ff',
        color: '#ffffff',
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
    };

    const overlayStyle: React.CSSProperties = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
    };

    const spinnerWrapperStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    };

    const spinnerStyle: React.CSSProperties = {
        width: 40,
        height: 40,
        borderRadius: '50%',
        border: '4px solid rgba(255,255,255,0.2)',
        borderTopColor: '#ffffff',
        animation: 'buyer-spin 0.9s linear infinite',
    };

    const spinnerLabelStyle: React.CSSProperties = {
        marginTop: 12,
        color: '#ffffff',
        fontSize: 14,
    };

    const qPinCircleStyle: React.CSSProperties = {
        width: 220,
        height: 220,
        borderRadius: '50%',
        backgroundColor: '#115c8e',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff',
        margin: '24px auto',
        position: 'relative',
        cursor: 'default',
        animation: 'qpin-pulse 1.2s ease-in-out infinite',
    };

    const qPinSmallStyle: React.CSSProperties = {
        fontSize: 20,
        marginBottom: 12,
        position: 'absolute',
        top: 20,
        left: '50%',
        transform: 'translateX(-50%)',
    };

    const qPinBigStyle: React.CSSProperties = {
        fontSize: 90,
        fontWeight: 700,
    };

    const amountSummaryStyle: React.CSSProperties = {
        fontSize: 20,
        display: 'flex',
        width: '95%',
        justifyContent: 'space-between',
        color: theme.text_03 ?? '#bbbbbb',
        textAlign: 'center',
    };

    const amountSummaryStrongStyle: React.CSSProperties = {
        fontWeight: 600,
        color: theme.text_01,
    };

    const approveModalOverlay: React.CSSProperties = {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2100,
    };

    const approveModalBox: React.CSSProperties = {
        width: '80%',
        maxWidth: 320,
        borderRadius: 16,
        backgroundColor: '#fff',
        padding: '20px 18px 16px',
        boxSizing: 'border-box',
        textAlign: 'center',
    };

    const approveModalText: React.CSSProperties = {
        fontSize: 16,
        fontWeight: 600,
        marginBottom: 16,
        color: '#000',
    };

    const approveModalButton: React.CSSProperties = {
        width: '100%',
        height: 40,
        borderRadius: 999,
        border: 'none',
        outline: 'none',
        backgroundColor: '#2f88ff',
        color: '#ffffff',
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
    };

    const maxBadgeRowStyle: React.CSSProperties = {
        marginTop: 10,
        marginBottom: 8,
        display: 'flex',
        justifyContent: 'flex-start',
    };

    const maxBadgeStyle: React.CSSProperties = {
        padding: '6px 10px',
        borderRadius: 5,
        backgroundColor: theme.card_background_01 ?? '#1f2126',
        fontSize: 12,
        color: theme.text_01,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        whiteSpace: 'nowrap',
    };

    const maxBadgeDimStyle: React.CSSProperties = {
        opacity: 0.8,
    };

    const maxBadgeStrongStyle: React.CSSProperties = {
        fontWeight: 600,
    };

    const modalBackground: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.background_alternative,
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1000,
    };

    return (
        <>
            <div style={containerStyle}>
                <div style={scaledRootStyle}>
                    <style>{`
        @keyframes buyer-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes qpin-pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.08); }
          100% { transform: scale(1); }
        }
        @keyframes cursor-blink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        @keyframes buyer-drop-in {
          from { transform: translateY(-24px); opacity: 0; }
          to   { transform: translateY(0);      opacity: 1; }
        }
        @keyframes buyer-fly-up {
          from { transform: translateY(0);      opacity: 1; }
          to   { transform: translateY(-24px);  opacity: 0; }
        }

        .buyer-key-btn {
          transition: transform 0.08s ease-out, background-color 0.12s ease-out;
          -webkit-tap-highlight-color: transparent;
        }
        .buyer-key-btn:active {
        }
        .buyer-key-btn:not(.decimal):active {
          background-color: #333333;
        }
      `}</style>

                    {/* QR 스캔 화면 */}
                    {step === 'scan' && (
                        <div style={scanOuterStyle}>
                            <div style={scanTitleStyle}>QR코드를 스캔해주세요</div>
                            <div style={qrFrameStyle} />
                            <div style={helperTextStyle}>
                                {isMobile ? (
                                    <>
                                        QR 스캔 화면이 자동으로 표시됩니다.
                                        <br />
                                        취소 후에는 아래 촬영 버튼으로 다시 스캔할 수 있어요.
                                    </>
                                ) : (
                                    <>
                                        PC에서는 QR 코드를 직접 촬영할 수 없습니다.
                                        <br />
                                        다음 버튼을 눌러 결제를 진행해 주세요.
                                    </>
                                )}
                            </div>

                            <div style={scanButtonsRow}>
                                {isMobile ? (
                                    <button
                                        style={secondaryButtonStyle}
                                        onClick={handleScanRetry}
                                    >
                                        촬영
                                    </button>
                                ) : (
                                    <button style={primaryButtonStyle} onClick={handleNext}>
                                        다음
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 금액 입력 / qPin */}
                    {step !== 'scan' && (
                        <div style={contentOuterStyle}>
                            <div style={headerStyle}>
                                <div style={headerLeftGroupStyle}>
                                    <div
                                        style={headerButtonStyle}
                                        onClick={handleBackNavigation}
                                    >
                                        {isQpinPhase ? (
                                            <X size={20} color={theme.text_01} />
                                        ) : (
                                            <ChevronLeft
                                                size={20}
                                                color={theme.text_01}
                                            />
                                        )}
                                    </div>
                                    <div style={coinCircleStyle}>
                                        <img
                                            src="/icons/elysium.png"
                                            alt="qr icon"
                                            style={coinImage}
                                            onError={(e) => {
                                                (
                                                    e.currentTarget as HTMLImageElement
                                                ).style.display = 'none';
                                            }}
                                        />
                                    </div>
                                    <div style={headerTitle}>{shop?.name ?? 'Elysium'}</div>
                                </div>
                                <div style={headerSpacerStyle} />
                            </div>

                            {/* 금액 입력 단계 */}
                            {step === 'amount' && shop && (
                                <div style={contentInnerFullHeight}>
                                    <div style={shopTopWrapper}>
                                        <img
                                            src={shop.imageUrl}
                                            alt={shop.name}
                                            style={shopImageAmountStyle}
                                        />

                                        {isCompactHeight ? (
                                            <div style={askRowCompactStyle}>
                                                <div style={askTextStyle}>
                                                    송금할 금액을 입력해주세요.
                                                </div>
                                                <div style={maxBadgeStyle}>
                                                    <span style={maxBadgeDimStyle}>
                                                        최대
                                                    </span>
                                                    <span style={maxBadgeStrongStyle}>
                                                        {shop.availableAmount}
                                                    </span>
                                                    <span style={maxBadgeDimStyle}>
                                                        가능
                                                    </span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={askTextStyle}>
                                                송금할 금액을 입력해주세요.
                                            </div>
                                        )}

                                        <div
                                            style={amountDisplayWrapper}
                                            ref={amountContainerRef}
                                        >
                                            {amount ? (
                                                <>
                                                    {summaryAmountStr
                                                        .split('')
                                                        .map((ch, idx, arr) => {
                                                            const lastIndex =
                                                                arr.length - 1;
                                                            const isLast =
                                                                idx === lastIndex;
                                                            const dotIdx =
                                                                summaryAmountStr.indexOf(
                                                                    '.'
                                                                );
                                                            const isDecimalChar =
                                                                dotIdx !== -1 &&
                                                                idx >= dotIdx;

                                                            const baseStyle =
                                                                isDecimalChar
                                                                    ? amountDisplayTextDec
                                                                    : amountDisplayTextInt;

                                                            const style: React.CSSProperties =
                                                            {
                                                                ...baseStyle,
                                                                ...(isLast &&
                                                                    lastAction ===
                                                                    'add'
                                                                    ? amountCharNewStyle
                                                                    : {}),
                                                            };

                                                            return (
                                                                <span
                                                                    key={`${ch}-${idx}-${arr.length}`}
                                                                    ref={
                                                                        isLast
                                                                            ? lastCharRef
                                                                            : null
                                                                    }
                                                                    style={style}
                                                                >
                                                                    {ch}
                                                                </span>
                                                            );
                                                        })}

                                                    <span
                                                        style={{
                                                            ...amountDisplayTextUnit,
                                                            color: theme.text_01,
                                                        }}
                                                    >
                                                        ELYM
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <span style={zeroPlaceholderStyle}>
                                                        0
                                                    </span>
                                                    <span
                                                        style={{
                                                            ...amountDisplayTextUnit,
                                                            color:
                                                                theme.text_03 ?? '#9f9f9f',
                                                        }}
                                                    >
                                                        ELYM
                                                    </span>
                                                </>
                                            )}

                                            {ghost && (
                                                <span
                                                    style={{
                                                        ...(ghost.isDecimal
                                                            ? amountDisplayTextDec
                                                            : amountDisplayTextInt),
                                                        ...ghostCharBaseStyle,
                                                        left: ghost.x,
                                                        top: ghost.y,
                                                    }}
                                                    onAnimationEnd={() =>
                                                        setGhost(null)
                                                    }
                                                >
                                                    {ghost.char}
                                                </span>
                                            )}
                                        </div>

                                        {!isCompactHeight && (
                                            <div style={maxBadgeRowStyle}>
                                                <div style={maxBadgeStyle}>
                                                    <span style={maxBadgeDimStyle}>
                                                        최대
                                                    </span>
                                                    <span style={maxBadgeStrongStyle}>
                                                        {shop.availableAmount}
                                                    </span>
                                                    <span style={maxBadgeDimStyle}>
                                                        가능
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div style={keypadAndButtonWrapper}>
                                        <div style={bottomButtonWrapper}>
                                            <button
                                                style={{
                                                    ...bottomButtonStyle,
                                                    opacity: hasAmount ? 1 : 0,
                                                    pointerEvents: hasAmount
                                                        ? 'auto'
                                                        : 'none',
                                                }}
                                                onClick={handlePay}
                                            >
                                                결제
                                            </button>
                                        </div>

                                        <div style={keypadWrapper}>
                                            <div style={keypadGrid}>
                                                {[
                                                    '1',
                                                    '2',
                                                    '3',
                                                    '4',
                                                    '5',
                                                    '6',
                                                    '7',
                                                    '8',
                                                    '9',
                                                    '.',
                                                    '0',
                                                    'backspace',
                                                ].map((key, idx) => {
                                                    if (key === '') {
                                                        return (
                                                            <div
                                                                key={idx}
                                                                style={keypadEmptyCell}
                                                            />
                                                        );
                                                    }
                                                    if (key === 'backspace') {
                                                        return (
                                                            <button
                                                                key={idx}
                                                                style={keypadButton}
                                                                className="buyer-key-btn"
                                                                onClick={() =>
                                                                    handleKeyPress(
                                                                        'backspace'
                                                                    )
                                                                }
                                                            >
                                                                ←
                                                            </button>
                                                        );
                                                    }

                                                    const extraStyle: React.CSSProperties =
                                                        key === '.' && hasDot
                                                            ? decimalKeyActiveStyle
                                                            : {};

                                                    return (
                                                        <button
                                                            key={idx}
                                                            style={{
                                                                ...keypadButton,
                                                                ...extraStyle,
                                                            }}
                                                            className={
                                                                key === '.'
                                                                    ? 'buyer-key-btn decimal'
                                                                    : 'buyer-key-btn'
                                                            }
                                                            onClick={() =>
                                                                handleKeyPress(key)
                                                            }
                                                        >
                                                            {key}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* qPin 단계 */}
                            {isQpinPhase && shop && (
                                <div style={contentInnerFullHeight}>
                                    <div style={shopTopWrapper}>
                                        <img
                                            src={shop.imageUrl}
                                            alt={shop.name}
                                            style={shopImageQpinStyle}
                                        />
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            paddingTop: 24,
                                            paddingBottom: 24,
                                        }}
                                    >
                                        {hasAmount && (
                                            <div style={amountSummaryStyle}>
                                                결제 금액:{' '}
                                                <span style={amountSummaryStrongStyle}>
                                                    <span
                                                        style={{
                                                            fontSize: 22,
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {intFormatted}
                                                    </span>
                                                    {hasDot && (
                                                        <>
                                                            <span
                                                                style={{
                                                                    fontSize: 16,
                                                                    fontWeight: 500,
                                                                    marginLeft: 2,
                                                                }}
                                                            >
                                                                .{decPart}
                                                            </span>
                                                            <span
                                                                style={{
                                                                    fontSize: 14,
                                                                    fontWeight: 500,
                                                                    marginLeft: 4,
                                                                }}
                                                            >
                                                                ELYM
                                                            </span>
                                                        </>
                                                    )}
                                                    {!hasDot && (
                                                        <span
                                                            style={{
                                                                fontSize: 14,
                                                                fontWeight: 500,
                                                                marginLeft: 4,
                                                            }}
                                                        >
                                                            ELYM
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                        {!hasAmount && <div style={{ height: 20 }} />}

                                        <div style={qPinCircleStyle}>
                                            <div style={qPinSmallStyle}>qPin</div>
                                            <div style={qPinBigStyle}>
                                                {qPin ?? '--'}
                                            </div>
                                        </div>

                                        <div style={qpinHelperTextStyle}>
                                            판매점에 위 qPin 번호를 알려주세요.
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <QposSocket />
                </div>

                {isApprovedModalOpen && (
                    <div style={approveModalOverlay}>
                        <div style={approveModalBox}>
                            <div style={approveModalText}>
                                판매자가 승인했습니다.
                            </div>
                            <button
                                style={approveModalButton}
                                onClick={handleApproveConfirm}
                            >
                                확인
                            </button>
                        </div>
                    </div>
                )}

                {isProcessing && (
                    <div style={overlayStyle}>
                        <div style={spinnerWrapperStyle}>
                            <div style={spinnerStyle} />
                            <div style={spinnerLabelStyle}>
                                결제 처리 중입니다…
                            </div>
                        </div>
                    </div>
                )}

                {showPinCodeAuth && (
                    <div style={modalBackground}>
                        <PinCodeScreen
                            onSuccess={handlePinCodeintAuthSuccess}
                            unlock={false}
                        />
                    </div>
                )}
            </div>

            {showFooter && (
                <div
                    style={{
                        width: '100%',
                        maxWidth: MAX_WIDTH,
                        height: FOOTER_HEIGHT,
                    }}
                >
                    <NavigationBar />
                </div>
            )}
        </>
    );
};

export default BuyerScreen;
